import { useRef, useState } from 'react';
import { ImagePlus, Film, X, Loader2 } from 'lucide-react';
import type { ListingMedia } from '../types';

const MAX_IMAGES = 50;
const MAX_IMAGE_SIZE_MB = 5;
const MAX_VIDEOS = 2;
const MAX_VIDEO_SIZE_MB = 25;

interface MediaUploaderProps {
  media: ListingMedia[];
  onUpload: (files: File[]) => Promise<ListingMedia[]>;
  onRemove: (url: string) => void;
  testId?: string;
}

/** Images (up to 50 @5MB) + videos (up to 2 @25MB) uploader shared by Bazaar and Classified create forms. */
export function MediaUploader({ media, onUpload, onRemove, testId }: MediaUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const imageCount = media.filter(m => m.type === 'image').length;
  const videoCount = media.filter(m => m.type === 'video').length;

  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    setError(null);

    const files = Array.from(fileList);
    const images = files.filter(f => !f.type.startsWith('video/'));
    const videos = files.filter(f => f.type.startsWith('video/'));

    const oversizedImage = images.find(f => f.size > MAX_IMAGE_SIZE_MB * 1024 * 1024);
    if (oversizedImage) { setError(`"${oversizedImage.name}" is over ${MAX_IMAGE_SIZE_MB}MB.`); return; }
    const oversizedVideo = videos.find(f => f.size > MAX_VIDEO_SIZE_MB * 1024 * 1024);
    if (oversizedVideo) { setError(`"${oversizedVideo.name}" is over ${MAX_VIDEO_SIZE_MB}MB.`); return; }
    if (imageCount + images.length > MAX_IMAGES) { setError(`You can upload at most ${MAX_IMAGES} photos.`); return; }
    if (videoCount + videos.length > MAX_VIDEOS) { setError(`You can upload at most ${MAX_VIDEOS} videos.`); return; }

    setUploading(true);
    try {
      await onUpload(files);
    } catch (e: any) {
      setError(e?.message ?? 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div data-testid={testId ?? 'media-uploader'}>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {media.map(m => (
          <div key={m.url} className="relative aspect-square rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
            {m.type === 'video' ? (
              <video src={m.url} className="w-full h-full object-cover" muted />
            ) : (
              <img src={m.url} alt="" className="w-full h-full object-cover" />
            )}
            {m.type === 'video' && (
              <span className="absolute bottom-1 left-1 bg-black/60 text-white rounded-full p-1">
                <Film className="w-3 h-3" />
              </span>
            )}
            <button
              type="button"
              data-testid={`media-remove-${m.url}`}
              onClick={() => onRemove(m.url)}
              className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}

        <button
          type="button"
          data-testid="media-uploader-add"
          onClick={() => inputRef.current?.click()}
          disabled={uploading || (imageCount >= MAX_IMAGES && videoCount >= MAX_VIDEOS)}
          className="aspect-square rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-1 text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors disabled:opacity-50"
        >
          {uploading ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <>
              <ImagePlus className="w-6 h-6" />
              <span className="text-xs font-medium">Add</span>
            </>
          )}
        </button>
      </div>

      <input
        ref={inputRef}
        data-testid="media-uploader-input"
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        onChange={e => handleFiles(e.target.files)}
      />

      <p className="text-xs text-gray-500 mt-2">
        {imageCount}/{MAX_IMAGES} photos (max {MAX_IMAGE_SIZE_MB}MB each) · {videoCount}/{MAX_VIDEOS} videos (max {MAX_VIDEO_SIZE_MB}MB each)
      </p>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}
